import { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** How long a collapsing block stays mounted: the length of the `closing` transitions in index.css. */
const CLOSE_MS = 150;

export type ExpandPhase = 'opening' | 'open' | 'closing';

/**
 * Re-measures a virtualized table's rendered rows. DataTable provides it, so a block that finishes
 * closing anywhere inside one of its rows (its own expanded block, or a nested tier within it) has the
 * rows below moved up before paint. Left to the virtualizer's ResizeObserver, a block unmounted from a
 * timer only reaches their offsets once a frame with a gap under the row has painted.
 */
export const RemeasureRowsContext = createContext<(() => void) | null>(null);

type Props = {
    open: boolean;
    /** Renders the block; `phase` goes on the element that animates, as `data-expand-phase`. */
    children: (phase: ExpandPhase) => ReactNode;
};

/**
 * A table row's expanded block, animated open and shut by `[data-expand-phase]` in index.css. It is
 * `opening` only when the user has just toggled it open, not when windowing remounts a row that is
 * already open, and it stays mounted while `closing`. The block holds its full height throughout, so
 * the rows around it move once, when it mounts or unmounts; only its paint is animated. Under reduced
 * motion it fades in and out in the new design, and appears and goes at once in classic.
 */
export const ExpandPresence = ({ open, children }: Props) => {
    const [phase, setPhase] = useState<ExpandPhase | null>(open ? 'open' : null);
    const [wasOpen, setWasOpen] = useState(open);
    if (open !== wasOpen) {
        setWasOpen(open);
        // Classic under reduced motion has no closing transition; the new design fades the block out.
        const goesAtOnce =
            document.documentElement.dataset.design !== 'new' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        setPhase(open ? 'opening' : goesAtOnce ? null : 'closing');
    }

    useEffect(() => {
        if (phase !== 'closing') return;
        const timer = setTimeout(() => setPhase(null), CLOSE_MS);
        return () => clearTimeout(timer);
    }, [phase]);

    const remeasureRows = useContext(RemeasureRowsContext);
    const hadBlock = useRef(phase !== null);
    useLayoutEffect(() => {
        if (phase === null && hadBlock.current) remeasureRows?.();
        hadBlock.current = phase !== null;
    }, [phase, remeasureRows]);

    return phase ? children(phase) : null;
};
